import { Link } from "react-router-dom";
import { BookOpen, Feather, Sparkles, Heart } from "lucide-react";
import { LANDING } from "@/constants/testIds/app";

const HERO_IMG =
  "https://images.unsplash.com/photo-1568990416308-c998af4cf9ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjb3p5JTIwcmVhZGluZyUyMG5vb2t8ZW58MHx8fHwxNzg2MDI2MjQ4fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#E8E3D5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img
              src="/logo.png"
              alt="Bibliotherapy Companion"
              className="w-11 h-11 object-contain"
            />
            <div className="leading-tight">
              <div className="font-serif-display text-[#2C2520] text-lg">Bibliotherapy</div>
              <div className="label-caps text-[0.6rem]">Companion</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              data-testid={LANDING.signIn}
              className="px-4 py-2 rounded-[12px] text-sm text-[#2C2520] hover:bg-[#F5F2EA] transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              data-testid={LANDING.getStarted}
              className="px-4 py-2 rounded-[12px] text-sm bg-[#4A5D4E] text-white hover:bg-[#3D4D40] transition-colors"
            >
              Begin
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="label-caps mb-6">A quiet reading nook</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#2C2520] leading-[1.05] tracking-tight">
            Books,
            <br />
            <span className="italic">chosen thoughtfully</span>
            <br />
            for how you feel.
          </h1>
          <p className="mt-7 text-[#2C2520]/75 text-base sm:text-lg leading-relaxed max-w-lg">
            Share a mood or a page from your journal. In return, receive a small, thoughtful set of
            books, essays, podcasts, and gentle practices, each with a note on why it was picked.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/signup"
              data-testid={LANDING.heroCta}
              className="px-6 py-3.5 rounded-[12px] bg-[#4A5D4E] text-white hover:bg-[#3D4D40] transition-transform duration-200 active:scale-95 inline-flex items-center gap-2 shadow-[0_2px_8px_rgba(44,37,32,0.06)]"
            >
              Start with a feeling
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link
              to="/login"
              className="px-6 py-3.5 rounded-[12px] border border-[#E8E3D5] text-[#2C2520] hover:bg-[#F5F2EA] transition-colors"
            >
              I have an account
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
            <Feature icon={Heart} label="Mood check-in" />
            <Feature icon={Feather} label="Journal reflection" />
            <Feature icon={BookOpen} label="Personal library" />
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 bg-[#C7BCA1]/20 rounded-[24px] -z-10" />
          <img
            src={HERO_IMG}
            alt="An open book beside a soft throw pillow in a warm reading nook"
            className="rounded-[16px] shadow-[0_8px_24px_rgba(44,37,32,0.12)] w-full object-cover aspect-[4/5]"
          />
          <div className="absolute -bottom-6 -left-6 bg-[#FDFBF7] border border-[#E8E3D5] rounded-[12px] p-4 shadow-[0_8px_24px_rgba(44,37,32,0.08)] max-w-[220px]">
            <div className="label-caps mb-1">Today's picks</div>
            <p className="font-serif-display italic text-[#2C2520] leading-snug">
              "A book must be the axe for the frozen sea within us."
            </p>
            <p className="text-xs text-[#2C2520]/60 mt-1">Kafka</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E8E3D5] py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4 text-sm text-[#2C2520]/55">
          <span>Made for readers who use books as care. Not medical advice.</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-[#2C2520]">Privacy</Link>
            <Link to="/disclosure" className="hover:text-[#2C2520]">Affiliate disclosure</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, label }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="w-10 h-10 rounded-[10px] bg-[#F5F2EA] border border-[#E8E3D5] flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#4A5D4E]" strokeWidth={1.5} />
      </div>
      <div className="text-sm text-[#2C2520]/80">{label}</div>
    </div>
  );
}
